/**
 * Draaidag-omschrijving met hiërarchie (Materialen, feedback Joris 30 sep 2026: "de omschrijving
 * voelt zonder hiërarchie en daarom onoverzichtelijk").
 *
 * De Outlook-titel is één lange regel: "OPTIE: Henk + Paul (b) || Enexis Project Venray - [9812] - LJ".
 * Hier splitsen we hem in:
 *   - labels:  "Optie", "Locatiebezoek" (als chip vóór de hoofdregel, niet als tekst)
 *   - hoofd:   wat/waar — het project-deel, zonder [nummer], PL-initialen of placeholders
 *   - meta:    crew compact, #projectnummer en PL-initialen (kleine grijze tweede regel)
 *
 * Hergebruikt de bestaande parsers: crewUitTitel/crewSamengevoegd + TIJDVAK (draaidagCrew),
 * initialenUitTitel (draaidagPl). kaleDraaidagNaam en isOptieNaam (draaidagenPlanning) gebruiken
 * we bewust níet: die knippen elk woord van ≤ 4 letters achteraan weg ("- Film") resp. zien elk
 * los woord "optie" als optie. Is de draaidag aan
 * een project gekoppeld, dan wint de projectnaam/het projectnummer uit de data van de titel.
 */
import { crewSamengevoegd, crewUitTitel, TIJDVAK } from './draaidagCrew';
import { initialenUitTitel } from './draaidagPl';

// Outlook-labels: een woord mét dubbele punt, vooraan de titel of direct na de crew
// ("OPTIE(2 dagen): …", "Edwin + Pepijn: Optie (1 dag): …"). Zonder dubbele punt of midden in
// een projectnaam ("Studio Brussel", "De beste optie voor morgen") is het géén label.
const LABEL_VOORAAN = /^\s*(optie|locatiebezoek|studio|recce)\b\s*(\([^)]*\))?\s*:\s*/i;
const TONEN = { optie: 'Optie', locatiebezoek: 'Locatiebezoek' };
// Crew en project scheiden: "||", "|", "//" (zelfde scheiders als crewUitTitel, zonder ":").
const PROJECT_SCHEIDER = /\s*(?:\|\||\/\/|\|)\s*/;
// Placeholders uit het Outlook-sjabloon die niemand invult: [NUMMER], [], "INITIAL PL".
const HAKEN = /\s*-?\s*\[[^\]]*\]/g;
const PLACEHOLDER_PL = /\s*-?\s*\binitial(?:en|s)?\s*pl\b/gi;
const PROJECTNUMMER = /\[(\d{3,6})\]/;
// Alleen echte initialen als PL-staart: "LJ", "TV", "JMV", "EvdB" — niet "Film" of "FILM".
const ECHTE_INITIALEN = /^(?:[A-Z]{2,3}|[A-Z][a-z]{1,3}[A-Z])$/;

const eerste = (v) => (Array.isArray(v) ? v[0] : v);
const lijst = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const voornaam = (v) => String(v || '').trim().toLowerCase().split(/\s+/)[0];

/** Titel zonder placeholders (voor de PL-initialen en het projectnummer). */
function zonderPlaceholders(titel) {
  return String(titel || '').replace(PLACEHOLDER_PL, '').replace(/\[\s*(?:nummer|nr|#)?\s*\]/gi, '').trim();
}

/** Tijdvak en labels vooraan weghalen ("(van 10:00 tot 12:00) OPTIE: LOCATIEBEZOEK: …"); labels verzamelen. */
function kopAf(s, labels) {
  // Eerst trimmen: na een crew-dubbele punt begint het stuk met een spatie (TIJDVAK is ^-verankerd).
  let t = String(s || '').trim().replace(TIJDVAK, '').trim();
  for (let i = 0; i < 4; i += 1) {
    const m = t.match(LABEL_VOORAAN);
    if (!m) break;
    if (TONEN[m[1].toLowerCase()]) labels.add(TONEN[m[1].toLowerCase()]);
    t = t.slice(m[0].length).trim().replace(TIJDVAK, '').trim();
  }
  return t.trim();
}

/** Project-deel opschonen: geen haken, geen (echte) PL-initialen achteraan, geen losse streepjes. */
function schoonHoofd(s, pl) {
  let t = String(s || '').replace(PLACEHOLDER_PL, '').replace(HAKEN, '');
  if (pl) t = t.replace(new RegExp(`(?:^|\\s)(?:-\\s*)?${pl}\\s*\\??\\s*$`), '');
  return t.replace(/\s{2,}/g, ' ').replace(/^[\s:|/-]+|[\s:|/-]+$/g, '').trim();
}

/**
 * Is dit stuk vóór de dubbele punt (of " / ") echt crew? Alleen met bewijs: een "+", een
 * (b)/(g)-markering, of een naam die als gekoppelde crew bekend is. "Enexis: Project Venray"
 * blijft zo heel.
 */
function isCrewDeel(voor, gekoppeld) {
  if (!voor || /[[\]\d]/.test(voor) || voor.length > 80) return false;
  const namen = crewUitTitel(`${voor} || x`);
  if (!namen.length) return false;
  // "+" is de crew-conventie; "&", "," of "en" komen ook in klantnamen voor ("Natuur & Milieu").
  if (/\+/.test(voor) || namen.some((n) => n.status)) return true;
  const bekend = new Set(gekoppeld.map(voornaam));
  return namen.some((n) => bekend.has(voornaam(n.naam)));
}

/**
 * Crew-deel en project-deel. Met "||", "|" of "//" is het project alles ná de eerste scheiding
 * (meerdere delen leesbaar met " · "); anders wordt alléén een aantoonbaar crew-deel vóór de
 * eerste dubbele punt afgesplitst ("Paul (b): Enexis: Project Venray" → "Enexis: Project Venray").
 */
function splits(titel, gekoppeld, labels) {
  const s = kopAf(titel, labels);
  const leesbaar = (x) => x.split(PROJECT_SCHEIDER).map((d) => d.trim()).filter(Boolean).join(' · ');
  // 1. Een aantoonbaar crew-deel vóór de eerste dubbele punt, als die vóór de eerste pipe staat:
  //    "Paul (b): Enexis | Project Venray" → project "Enexis · Project Venray".
  const i = s.indexOf(':');
  const pipe = s.search(PROJECT_SCHEIDER);
  const slash = s.search(/\s+\/\s+/);
  if (i > 0 && (pipe < 0 || i < pipe) && (slash < 0 || i < slash) && isCrewDeel(s.slice(0, i).trim(), gekoppeld)) {
    return { crewDeel: s.slice(0, i).trim(), project: leesbaar(kopAf(s.slice(i + 1), labels)) };
  }
  // 1b. " / " als scheider (crewUitTitel kent hem) — alleen met aantoonbare crew ervoor,
  //     want " / " staat ook in projectnamen.
  if (slash > 0 && (pipe < 0 || slash < pipe) && (i < 0 || slash < i) && isCrewDeel(s.slice(0, slash).trim(), gekoppeld)) {
    return { crewDeel: s.slice(0, slash).trim(), project: leesbaar(kopAf(s.slice(s.indexOf('/', slash) + 1), labels)) };
  }
  // 2. Anders de klassieke scheiding "crew || project" (ook "|" en "//").
  const delen = s.split(PROJECT_SCHEIDER).map((x) => x.trim()).filter(Boolean);
  if (delen.length > 1) {
    const crewDeel = delen[0];
    // Label tussen dubbele punten in het crew-deel: "Fotograaf: OPTIE: Fotograaf | …"
    crewDeel.split(':').map((x) => x.trim()).forEach((d) => {
      const m = d.match(/^(optie|locatiebezoek)\b\s*(?:\([^)]*\))?$/i);
      if (m) labels.add(TONEN[m[1].toLowerCase()]);
    });
    return { crewDeel, project: kopAf(delen.slice(1).join(' · '), labels) };
  }
  return { crewDeel: '', project: s };
}

/** Gekoppelde crew + externe namen, met de (b)/(g)/? uit de titel ook bij gekoppelde namen. */
function crewMetMarkering(gekoppeld, crewTitel) {
  const samen = crewSamengevoegd(gekoppeld, crewTitel);
  const status = new Map(crewUitTitel(crewTitel).filter((x) => x.status).map((x) => [voornaam(x.naam), x.status]));
  const teken = { bevestigd: ' (b)', gevraagd: ' (g)', onzeker: '?' };
  return [
    ...samen.gekoppeld.map((n) => `${n}${teken[status.get(voornaam(n))] || ''}`),
    ...samen.alle.slice(samen.gekoppeld.length),
  ];
}

/**
 * @param titel          originele Outlook-titel (Airtable-veld Draaidag)
 * @param weergaveNaam   handmatige weergavenaam uit de materiaaltool (wint voor de hoofdregel)
 * @param projectnaam    'Projectnaam (from Project)' als de draaidag aan een project hangt
 * @param projectnummer  'Projectnummer (from Project)'
 * @param crew           gekoppelde crew ('Naam (from Crew)') — aangevuld met (b)/(g) uit de titel
 * @returns {{ hoofd, labels, optie, crew, projectnummer, pl, meta }}
 */
export function draaidagOmschrijving({ titel = '', weergaveNaam = '', projectnaam = '', projectnummer = '', crew = [] } = {}) {
  const ruw = String(titel || '').trim();
  const handmatig = String(weergaveNaam || '').trim();
  const gekoppeld = lijst(crew).filter((n) => typeof n === 'string' && n.trim() && !n.startsWith('rec'));
  const gevonden = new Set();

  const schoon = zonderPlaceholders(ruw);
  const { crewDeel, project: projectUitTitel } = splits(schoon, gekoppeld, gevonden);
  // Labels komen alleen uit label-syntaxis mét dubbele punt (kopAf/splits); een los woord
  // "optie" (isOptieNaam matcht elk voorkomen) zou een projectnaam als optie markeren.
  const handmatigSchoon = handmatig ? kopAf(handmatig, gevonden) : '';
  const labels = ['Optie', 'Locatiebezoek'].filter((l) => gevonden.has(l));

  // PL-staart: "… - [9812] - LJ" (initialenUitTitel) of zonder streepje "… [8228] AV".
  const kandidaatPl = initialenUitTitel(schoon) || (schoon.match(/\[\d{3,6}\]\s*([A-Za-z]{2,4})\s*\??\s*$/)?.[1] || '');
  const pl = ECHTE_INITIALEN.test(kandidaatPl) ? kandidaatPl : '';
  const nummer = String(eerste(projectnummer) || '').trim() || (schoon.match(PROJECTNUMMER)?.[1] || '');

  const project = String(eerste(projectnaam) || '').trim();
  let hoofd = '';
  // Handmatige naam: alleen labels vooraan en placeholders weg, verder zoals ingevuld.
  if (handmatigSchoon) hoofd = schoonHoofd(handmatigSchoon, '');
  // Projectdata: ongemoeid laten (geen label- of PL-verwijdering in een echte projectnaam).
  if (!hoofd && project) hoofd = project;
  if (!hoofd) hoofd = schoonHoofd(projectUitTitel, pl) || schoonHoofd(crewDeel, '') || 'Draaidag';

  // Crew uit het crew-deel (labels en tijdvak al weg), zodat "OPTIE: LOCATIEBEZOEK: Michel || …" Michel geeft.
  const crewNamen = crewMetMarkering(gekoppeld, crewDeel ? `${crewDeel} || x` : '');
  const meta = [crewNamen.join(' + '), nummer ? `#${nummer}` : '', pl].filter(Boolean).join(' · ');
  return { hoofd, labels, optie: labels.includes('Optie'), crew: crewNamen, projectnummer: nummer, pl, meta };
}

export default draaidagOmschrijving;
